import type { FC } from 'react';
import { memo, useEffect } from 'react';
import { Box, Grid } from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';

import type { DashboardConfig } from 'src/types/dashboard';
import { guidedTourState } from 'src/components/app/context/GuidedTourContext/atom';
import { GuidedTourState } from 'src/components/app/context/GuidedTourContext/constants';
import { chatbotVisibilityState } from 'src/components/chatbot/atoms';
import { ChatbotState } from 'src/components/chatbot/constants';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import {
  CodingResultsContextProvider,
  useCodingResults,
} from 'src/components/dashboard/DashboardContext/CodingResultsContext';
import StatisticsContextProvider, {
  useStatistics,
} from 'src/components/dashboard/DashboardContext/StatisticsContext';
import {
  TagCloudContextProvider,
  useTagCloud,
} from 'src/components/dashboard/DashboardContext/TagCloudContext';
import {
  dashboardContentLoading,
  dashboardHiddenSections,
} from 'src/components/dashboard/DashboardPage/atom';
import DashboardHeader from 'src/components/dashboard/DashboardPage/components/DashboardHeader';
import WidgetWrapper from 'src/components/dashboard/DashboardPage/components/WidgetWrapper';
import { hasAggregates } from 'src/components/dashboard/helper';

export const DASHBOARD_CONTENT_TEST_ID = 'dashboard-content-root';

const DashboardContent: FC = () => {
  const { widgetConfigs } = useDashboardContext();
  const { data: statisticsData } = useStatistics();
  const { pending: tagResultsPending } = useTagCloud();
  const { pending: codingResultsPending } = useCodingResults();
  const setChatbotVisibility = useSetAtom(chatbotVisibilityState);
  const [guidedTourActiveStatus] = useAtom(guidedTourState);
  const setContentLoading = useSetAtom(dashboardContentLoading);

  useEffect(() => {
    if (hasAggregates(statisticsData)) {
      setChatbotVisibility(ChatbotState.VISIBLE);
    } else {
      setChatbotVisibility(ChatbotState.DISABLED);
    }
  }, [statisticsData]);

  useEffect(() => {
    if (statisticsData && !tagResultsPending && !codingResultsPending) {
      setContentLoading(false);
    } else {
      setContentLoading(true);
    }
  }, [statisticsData, tagResultsPending, codingResultsPending]);

  // set chatbot to hidden when guided tour is on active/autostart
  useEffect(() => {
    if (
      [GuidedTourState.ACTIVE, GuidedTourState.AUTOSTART].includes(
        guidedTourActiveStatus,
      )
    ) {
      setChatbotVisibility(ChatbotState.HIDDEN);
    }
  });

  const setHiddenSections = useSetAtom(dashboardHiddenSections);
  // Reset hidden section on unmount so the next module can use the state
  useEffect(() => setHiddenSections([]), []);

  return (
    <Box data-test-id={DASHBOARD_CONTENT_TEST_ID}>
      <Grid container>
        {widgetConfigs.map((config) => (
          <WidgetWrapper
            config={config}
            isFirstOfWidgetType={config.isFirstOfWidgetType}
            key={config.id}
            sectionId={config.currentSection}
          />
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardContent;

const DashboardContentWrapper = ({
  dashboardConfig,
  showFollowUpInspirations,
}: {
  dashboardConfig: DashboardConfig;
  showFollowUpInspirations: boolean;
}) => (
  <StatisticsContextProvider>
    <TagCloudContextProvider>
      <CodingResultsContextProvider>
        <DashboardHeader
          dashboardConfig={dashboardConfig}
          showFollowUpInspirations={showFollowUpInspirations}
        />
        <DashboardContent />
      </CodingResultsContextProvider>
    </TagCloudContextProvider>
  </StatisticsContextProvider>
);

export const DashboardContentWrapperMemo = memo(DashboardContentWrapper);
